<template>
    <div class="carousel" ref="carousel" :class="{'carousel--overlay': overlay, 'carousel--hide-pips': !showPips}"  v-if="type != 'card'">
        <slot></slot>
        <div class="carousel__slides">
            <img v-if="type == 'Image'" v-for="(item, index) in items" class="carousel__item" :src="item" :alt="`${alt} #${index + 1}`" :class="{'is-selected': index === currentSlideIndex}" />
        </div>

        <CarouselControl
            class="carousel__controls"
            v-if="(items?.length > 1) && type == 'Image'"
            v-model:currentSlideIndex="currentSlideIndex"
            :slide-count="items?.length"
             />
    </div>
    <div class="carousel" ref="cardCarousel" :class="{'carousel--overlay': overlay, 'carousel--hide-pips': !showPips}"  v-if="type == 'card'">
        <div class="carousel__slides">
            <RoomCard v-for="(item, index) in items"  :data="item" :selected-year-offset="selectedYearOffset" class="search-results__item carousel__item"
                    :current-academic-year="currentAcademicYear" :next-academic-year="nextAcademicYear" :class="{'is-selected': index === currentSlideIndex}"/>
        </div>
    </div>
    <CarouselControl
            class="carousel__controls featured"
            v-if="(items?.length > 1) && type == 'card'"
            v-model:currentSlideIndex="currentSlideIndex"
            :slide-count="items?.length"
             />
</template>
<script setup>
import CarouselControl from '@components/CarouselControl.vue';
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
    // The images to display
    items: {
        type: Array,
        required: true
    },
    // The alt text for the images
    alt: {
        type: String,
        default: 'Image'
    },
    // Whether to display the navigation overlaid on the images
    overlay: {
        type: Boolean,
        default: false
    },
    // Whether or not to show pips
    showPips: {
        type: Boolean,
        default: true
    },
    type:{
        type: String,
        default: 'Image'
    },
    // The selected year offset, e.g. 0 for current year, 1 for next year
    selectedYearOffset: {
        type: Number,
        default: 1
    },
    // The current academic year range as a string, e.g. 2023/24
    currentAcademicYear: {
        type: String,
        required: true
    },
    // The next academic year range as a string, e.g. 2024/25
    nextAcademicYear: {
        type: String,
        required: true
    }
});

// The carousel element
const carousel = ref(null);
const cardCarousel = ref(null);

// The slides element
let slides = null;

// The size of the carousel
const size = reactive({ width: 0, height: 0 });

// The current slide index
const currentSlideIndex = ref(0);

// Debounced handler for resize
let resizeTimeout = null;

// Handle the resize event to update the size of the carousel and its items
const handleResize = () => {
    clearTimeout(resizeTimeout);

    resizeTimeout = setTimeout(() => {
        const width = carousel?.value?.clientWidth;
        const height = carousel?.value?.clientHeight;
        if (!width || !height) return;
        size.width = width;
        size.height = height;
    }, 100);
}

// The size CSS custom props for the carousel
const sizeProps = computed(() => {
    return {
        "--carousel-width": `${size.width}px`,
        "--carousel-height": `${size.height}px`
    }
});

// Debounced timeout for scroll handler
let scrollTimeout = null;

// Handle the scroll event to update the current slide index when scrolling
const handleScroll = () => {
    clearTimeout(scrollTimeout);

    scrollTimeout = setTimeout(() => {
        // figure out which slide is selected by the scroll position
        const slideWidth = slides.scrollWidth / props.items?.length;
        const slideIndex = Math.round(slides.scrollLeft / slideWidth);
        currentSlideIndex.value = slideIndex;
    }, 100);
}

// Watch for changes to the current slide index and scroll the carousel if
// necessary
watch(currentSlideIndex, () => {
    setTimeout(() => {
        if(props.type == "Image"){
            const item = carousel.value.querySelector('.carousel__item.is-selected');
            if (item && slides) {
                if (item.offsetLeft - slides.scrollLeft < 0) slides.scrollBy(item.offsetLeft  - slides.scrollLeft, 0);
                else if (item.offsetLeft + item.offsetWidth - slides.scrollLeft > slides.clientWidth) slides.scrollBy(item.offsetLeft + item.offsetWidth - slides.scrollLeft - slides.clientWidth, 0);
            }
        }
        else if(props.type == "card"){
            const item = cardCarousel.value.querySelector('.search-results__item.is-selected');
            if (item && slides) {
                if (item.offsetLeft - slides.scrollLeft - 10 < 0) slides.scrollBy(item.offsetLeft - 20 - slides.scrollLeft, 0);
                else if (item.offsetLeft + item.offsetWidth + 10 - slides.scrollLeft > slides.clientWidth) slides.scrollBy(item.offsetLeft + (item.offsetWidth + 20) - slides.scrollLeft - slides.clientWidth, 0);
            }
        }
    }, 0);
});

// Safari doesn't support scrollend, so we use scroll and a timeout
const scrollEvent = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ? "scroll" : "scrollend";

onMounted(() => {
    // Update the size when the window is resized
    // window.addEventListener('resize', handleResize);

    // Get a reference to the slides element
    slides = props.type == "Image" ? carousel.value.querySelector('.carousel__slides') : cardCarousel.value.querySelector('.carousel__slides');

    // Listen for scrolls and update the current slide index
    slides.addEventListener(scrollEvent, handleScroll, { passive: true });
});

onUnmounted(() => {
    // Clean up after ourselves as we have some quite spicy event listeners
    window.removeEventListener('resize', handleResize);
    slides.removeEventListener(scrollEvent, handleScroll);
})

</script>